/**
 * Smart API: Retrieve Tags feature.
 * (2026-09-28, Jason Cox) Lets a vendor pull an item back OUT of a smartbox so
 * it is recorded as a supplier return (a negative-quantity transfer ticket,
 * "TT...") instead of being billed to the customer as a consumption event
 * (a delivery ticket, "DT...").
 *
 * How retrieve works in Smart -- it is a TWO-step process:
 *   1. Flag: editAppPDAPreReturn sets md_rfid.pre_return = 1. Nothing else
 *      changes yet -- no ticket, no inventory movement.
 *   2. Remove: the vendor physically takes the item out of the box. On the
 *      next smartbox scan the missing tag is seen as flagged, so the scan
 *      pipeline (rebound-smart-laravel UndetectedTagsHandler::handle_prereturns)
 *      writes the negative transfer ticket and resets the tag to PENDING so it
 *      can be matched again. An UNflagged missing tag would instead be billed
 *      as a consumption -- which is why the flag has to come first.
 *
 * Endpoints (legacy PDA-app endpoints, reused as-is; no Smart changes):
 *   POST /md/v1/getAppPdaRetrieveRfid  -- per-tag eligibility check
 *   POST /md/v1/editAppPDAPreReturn    -- set the retrieve flag
 *   POST /scm/v1/rfid/lookupTags       -- enrichment (smartbox name, qty)
 *
 * Reason for checking every tag before flagging: editAppPDAPreReturn itself
 * performs NO validation (no ownership check, no status check, and it reports
 * success even for an EPC that doesn't exist). getAppPdaRetrieveRfid is the
 * only place Smart enforces "vendors may only touch their own tags", so the
 * PWA must never flag a tag that hasn't passed it.
 */
import { fetchWithRetry } from '../../utils/fetchWithRetry';
import type { ApiConfig } from '../apiConfig';
import { lookupCheckoutTags } from './manualCheckoutApi';

/** Smart return codes the retrieve check can answer with (ReturnCode.php). */
const CODE_SUCCESS = 0;
const CODE_NO_AUTHORITY = 10006; // vendor does not own this tag
const CODE_ILLEGAL = 10007;      // tag already consumed
const CODE_NO_DATA = 10008;      // EPC not in the system
const CODE_PRE_RETURN = 10010;   // tag already flagged for retrieval

/** md_rfid.status value for "sitting in a smartbox" (RFID_STATUS_IN_SMARTBOX). */
export const RFID_STATUS_IN_SMARTBOX = 3;

/** Why a scanned tag cannot be retrieved. */
export type RetrieveBlockReason =
  | 'already_flagged'
  | 'consumed'
  | 'not_yours'
  | 'not_found'
  | 'not_in_smartbox'
  | 'error';

/** The result of checking one scanned tag. */
export interface RetrieveTagCheck {
  /** The scanned EPC. */
  rfid: string;
  /** True when the tag can be flagged for retrieval right now. */
  eligible: boolean;
  /** Set when `eligible` is false. */
  reason?: RetrieveBlockReason;
  /** Server message for `reason: 'error'`. */
  message?: string;
  /** md_rfid.status (3 = in smartbox). */
  status?: number;
  sku?: string;
  itemName?: string;
  smartboxName?: string;
  /** Number of units on the tag; the return ticket is for -packageQty. */
  packageQty?: number;
}

/**
 * Ask Smart whether one tag may be retrieved by the logged-in user.
 *
 * @param config - Smart API config (base URL + auth headers)
 * @param rfid - EPC to check
 * @returns the check result; never throws for an API-level "no" answer
 */
async function checkOne(config: ApiConfig, rfid: string): Promise<RetrieveTagCheck> {
  let data: any;
  try {
    const response = await fetchWithRetry(
      `${config.baseUrl}/md/v1/getAppPdaRetrieveRfid`,
      {
        method: 'POST',
        headers: config.authHeaders,
        body: JSON.stringify({ rfid }),
      },
      { maxRetries: 2, timeout: 10000 },
    );
    data = await response.json();
  } catch (err: any) {
    return { rfid, eligible: false, reason: 'error', message: err?.message || 'check failed' };
  }

  const code = Number(data?.code);
  if (code === CODE_PRE_RETURN) return { rfid, eligible: false, reason: 'already_flagged' };
  if (code === CODE_ILLEGAL) return { rfid, eligible: false, reason: 'consumed' };
  if (code === CODE_NO_AUTHORITY) return { rfid, eligible: false, reason: 'not_yours' };
  if (code === CODE_NO_DATA) return { rfid, eligible: false, reason: 'not_found' };
  if (code !== CODE_SUCCESS) {
    return { rfid, eligible: false, reason: 'error', message: data?.msg || `code ${data?.code}` };
  }

  const info = data.data || {};
  const status = info.status !== undefined && info.status !== null ? Number(info.status) : undefined;
  return {
    rfid,
    // Reason: Smart's check only rejects consumed tags. A PENDING tag (matched
    // but never placed in a box) or a manually checked-out one would also pass
    // it, but flagging those is meaningless -- the return ticket is only ever
    // written when a tag goes missing FROM a smartbox scan.
    eligible: status === RFID_STATUS_IN_SMARTBOX,
    reason: status === RFID_STATUS_IN_SMARTBOX ? undefined : 'not_in_smartbox',
    status,
    sku: info.sku || undefined,
    itemName: info.itemsName || undefined,
  };
}

/**
 * Check a batch of scanned tags for retrieval.
 *
 * Runs the authoritative per-tag Smart check for every EPC, then enriches the
 * rows with the smartbox name and package quantity from lookupTags. The
 * enrichment is best-effort: if it fails the checks are still returned.
 *
 * @param config - Smart API config
 * @param rfids - EPCs to check
 * @returns one result per EPC, in the same order
 */
export async function checkRetrieveTags(
  config: ApiConfig,
  rfids: string[],
): Promise<RetrieveTagCheck[]> {
  if (rfids.length === 0) return [];

  const [checks, details] = await Promise.all([
    Promise.all(rfids.map((rfid) => checkOne(config, rfid))),
    lookupCheckoutTags(config, rfids).catch(() => []),
  ]);

  return checks.map((check) => {
    const detail = details.find((d) => d.rfid === check.rfid);
    if (!detail) return check;
    return {
      ...check,
      sku: check.sku || detail.sku,
      itemName: check.itemName || detail.itemName,
      smartboxName: detail.smartboxName || undefined,
      packageQty: detail.packageQty !== undefined ? Number(detail.packageQty) : undefined,
    };
  });
}

/**
 * Flag tags for retrieval (sets md_rfid.pre_return = 1 in one transaction).
 *
 * Safe to retry: the write only sets a flag to 1, so repeating it changes
 * nothing. Callers MUST have run checkRetrieveTags first -- this endpoint does
 * no validation of its own (see the file header).
 *
 * @param config - Smart API config
 * @param rfids - EPCs that passed checkRetrieveTags
 * @throws Error when the request fails or Smart reports an error
 */
export async function flagTagsForRetrieval(config: ApiConfig, rfids: string[]): Promise<void> {
  if (rfids.length === 0) return;
  const response = await fetchWithRetry(
    `${config.baseUrl}/md/v1/editAppPDAPreReturn`,
    {
      method: 'POST',
      headers: config.authHeaders,
      // Reason: the controller does explode(',', rfid), so it takes a single
      // comma-separated string, not a JSON array.
      body: JSON.stringify({ rfid: rfids.join(','), smartboxId: '0', workcenterId: '0' }),
    },
    { maxRetries: 2, timeout: 30000 },
  );
  const data = await response.json();
  if (Number(data?.code) !== CODE_SUCCESS) {
    throw new Error(data?.msg || 'editAppPDAPreReturn failed');
  }
}
